import { Button, Font, Modal } from "@moneyforward/mfx_biz_react_components";
import { css } from "@emotion/react";

import { SelectRowItemCell, SelectRowContainer } from "@/components/atoms";
import { color } from "@/styles/theme";
import { BankServices, Plan, Service } from "@/generated/graphql_billing";
import { filterActiveOtherServices } from "@/libs/billing/service";
import { PlanServiceCell } from "@/components/molecules/PlanServiceCell";

type Props = {
  active: boolean;
  plan?: Plan;
  services: BankServices["services"];
  onClickClose: () => void;
};

export const PlanDetailModal = ({
  active,
  plan,
  services,
  onClickClose,
}: Props) => {
  const displayPlanRowItemCell = (
    services: Service[],
    targetService: Service,
    displayIndex: number
  ) => {
    const rowItemData = services.find(
      (service) => service.id === targetService.id
    );
    return (
      <SelectRowItemCell
        key={`SelectRowItemCell-${displayIndex}`}
        externalStyleClass={css([
          { ...externalStyleClass },
          displayIndex % 2 === 0 ? null : innerStyleItemClass,
        ])}
        rightBorderDisplay={false}
        backgroundColor={displayIndex % 2 === 0 ? color.gray100 : color.white}
      >
        <PlanServiceCell service={rowItemData} />
      </SelectRowItemCell>
    );
  };

  const addButton = (
    <>
      <Button type="button" variant="primary" onClick={onClickClose}>
        閉じる
      </Button>
    </>
  );

  if (!active) {
    return <></>;
  }

  const otherServices = filterActiveOtherServices(plan?.services);

  const displayOtherServiceRowItemCell = (otherServices: Service[]) => {
    if (otherServices.length === 0) {
      return <></>;
    }
    return (
      <SelectRowItemCell
        externalStyleClass={css(externalStyleClass, {
          gridColumn: "1 / -1",
        })}
        css={{
          justifyContent: "flex-start",
          padding: "8px 16px",
          whiteSpace: "pre-wrap",
        }}
      >
        <Font variant="s">
          その他サービス <br />
          {otherServices.map((service) => (
            <Font key={service.id} variant="s">
              ・{service.name}
            </Font>
          ))}
        </Font>
      </SelectRowItemCell>
    );
  };

  const planDetailModalBodyNode = (
    <div
      css={{
        maxHeight: 528,
        display: "flex",
        flexDirection: "column",
        padding: "8px 0px",
        gap: "8px",
      }}
    >
      <Font variant="mb">利用可能なサービス</Font>
      <SelectRowContainer
        minWidth={416}
        gridTemplateColumns={`${services.map((_) => "1fr").join(" ")}`}
      >
        {services.map((service, idx) => (
          <SelectRowItemCell
            key={service.id}
            flexDirection="column"
            backgroundColor={(idx + 1) % 2 === 0 ? color.gray100 : "inherit"}
            externalStyleClass={css([
              (idx + 1) % 2 === 1 ? innerStyleHeaderClass : null,
            ])}
          >
            {service.name.split(" ").map((nameWord, index) => (
              <Font variant="s" key={index}>
                {nameWord}
              </Font>
            ))}
          </SelectRowItemCell>
        ))}
        {services.map((service, index) =>
          displayPlanRowItemCell(plan?.services, service, index + 1)
        )}
        {displayOtherServiceRowItemCell(otherServices)}
      </SelectRowContainer>
      <div
        css={{
          flexShrink: 1,
          whiteSpace: "pre-wrap",
          overflowY: "auto",
        }}
      >
        <Font>{plan?.description}</Font>
      </div>
    </div>
  );

  return (
    <Modal
      css={{ minWidth: 464 }}
      title={plan?.name.concat("詳細")}
      body={planDetailModalBodyNode}
      zIndex={5}
      footerButtons={addButton}
      onClose={onClickClose}
      backgroundColor={color.backgroundGray}
    />
  );
};

const externalStyleClass = css({
  borderTopWidth: 1,
  borderStyle: "solid",
  borderColor: color.iron,
});

const innerStyleHeaderClass = css({
  borderTopLeftRadius: "inherit",
  borderTopRightRadius: "inherit",
});

const innerStyleItemClass = css({
  borderBottomLeftRadius: "inherit",
  borderBottomRightRadius: "inherit",
});
